if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        // Check if variant ID is valid
        const variantId = this.variantIdInput.value;
        if (!variantId) {
          console.error("No variant ID found");
          this.handleErrorMessage(
            "Product configuration error. Please refresh the page."
          );
          return;
        }

        console.log("Variant ID:", variantId);
        console.log("Variant input disabled:", this.variantIdInput.disabled);

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        // Safari-specific fixes
        const isSafari =
          /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          /Safari/.test(navigator.userAgent);
        if (isSafari) {
          // Add Safari-specific headers
          config.headers["Accept"] = "application/json, text/plain, */*";
          config.headers["Cache-Control"] = "no-cache";
          // Ensure proper credentials handling
          config.credentials = "same-origin";
        }

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        // Log the form data being sent
        console.log("Form data being sent:");
        for (let [key, value] of formData.entries()) {
          console.log(`${key}: ${value}`);
        }

        config.body = formData;

        // Add timeout for Safari
        const timeoutDuration = isSafari ? 15000 : 10000; // 15 seconds for Safari
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(
            () => reject(new Error("Request timeout")),
            timeoutDuration
          );
        });

        Promise.race([fetch(`${routes.cart_add_url}`, config), timeoutPromise])
          .then((response) => {
            console.log("Cart add response status:", response.status);
            console.log("Cart add response headers:", response.headers);

            if (!response.ok) {
              // Log the actual error response
              return response.text().then((errorText) => {
                console.error("Server error response:", errorText);
                throw new Error(
                  `HTTP ${response.status}: ${response.statusText} - ${errorText}`
                );
              });
            }
            return response.json();
          })
          .then((response) => {
            console.log("Cart add success response:", response);

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                // Use Safari-specific method if available
                if (typeof this.cart.handleSafariCartUpdate === "function") {
                  this.cart.handleSafariCartUpdate(response);
                } else {
                  this.cart.renderContents(response);
                }
              });
            }
          })
          .catch((e) => {
            console.error("Cart add error:", e);
            console.error("Error details:", {
              name: e.name,
              message: e.message,
              stack: e.stack,
            });

            // Enhanced error handling for Safari
            let errorMessage = "Please try again in a few minutes";

            if (e.name === "TypeError" && e.message.includes("fetch")) {
              errorMessage =
                "Network error. Please check your connection and try again.";
            } else if (e.message.includes("timeout")) {
              errorMessage = "Request timed out. Please try again.";
            } else if (e.message.includes("HTTP")) {
              // Extract specific HTTP error details
              const httpMatch = e.message.match(/HTTP (\d+): (.+)/);
              if (httpMatch) {
                const statusCode = httpMatch[1];
                const statusText = httpMatch[2];

                if (statusCode === "422") {
                  errorMessage =
                    "Product configuration error. Please refresh the page and try again.";
                } else if (statusCode === "429") {
                  errorMessage =
                    "Too many requests. Please wait a moment and try again.";
                } else if (statusCode === "500") {
                  errorMessage =
                    "Server error. Please try again in a few minutes.";
                } else {
                  errorMessage = `Server error (${statusCode}). Please try again.`;
                }
              } else {
                errorMessage =
                  "Server error. Please try again in a few minutes.";
              }
            } else if (isSafari) {
              errorMessage =
                "Safari browser issue. Please try refreshing the page.";
            }

            // Show error message to user
            this.handleErrorMessage(errorMessage);

            // Publish error event
            publish(PUB_SUB_EVENTS.cartError, {
              source: "product-form",
              productVariantId: formData.get("id"),
              errors: [errorMessage],
              message: errorMessage,
            });

            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;

          // Auto-hide error message after 5 seconds
          setTimeout(() => {
            this.errorMessageWrapper.toggleAttribute("hidden", true);
          }, 5000);
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
